/**
 * Spec 028: the "Region" card on `/admin/brand-settings`. The brand's country,
 * currency and units; only the platform owner changes them (the server refuses
 * brand admins, and refuses a new currency once the brand has trips). Plain
 * functions, tested in `regionAdmin.test.ts`.
 */
import { formatMoney } from '../../utils/money';
import { convertLimit, convertPricePerUnit, DistanceUnit, formatDistance } from '../../utils/units';

type Rules = Record<string, any> | null | undefined;

export type Country = 'US' | 'CA';
export type CurrencyCode = 'USD' | 'CAD';
export type MeasurementSystem = 'imperial' | 'metric';

export interface RegionForm {
  country: Country;
  currency: CurrencyCode;
  measurementSystem: MeasurementSystem;
}

export const COUNTRY_LABELS: Record<Country, string> = {
  US: 'United States',
  CA: 'Canada',
};

export const CURRENCY_LABELS: Record<CurrencyCode, string> = {
  USD: 'USD (US dollar)',
  CAD: 'CAD (Canadian dollar)',
};

export const MEASUREMENT_LABELS: Record<MeasurementSystem, string> = {
  imperial: 'Imperial (miles, sq ft)',
  metric: 'Metric (km, m²)',
};

/** What the brand has now; a missing key is today's default. */
export function regionFormOf(rules: Rules): RegionForm {
  return {
    country: rules?.country === 'CA' ? 'CA' : 'US',
    currency: rules?.currency === 'CAD' ? 'CAD' : 'USD',
    measurementSystem: rules?.measurementSystem === 'metric' ? 'metric' : 'imperial',
  };
}

/** Choosing a country suggests its currency and units (each can be changed back). */
export function regionSuggestion(country: Country): RegionForm {
  return country === 'CA'
    ? { country, currency: 'CAD', measurementSystem: 'metric' }
    : { country, currency: 'USD', measurementSystem: 'imperial' };
}

/** Only the fields that change, for `updateBrandRules`. Null when nothing changes. */
export function regionPatch(loaded: RegionForm, next: RegionForm): Partial<RegionForm> | null {
  const patch: Partial<RegionForm> = {};
  (Object.keys(next) as Array<keyof RegionForm>).forEach(key => {
    if (next[key] !== loaded[key]) (patch as Record<string, string>)[key] = next[key];
  });
  return Object.keys(patch).length ? patch : null;
}

const unitOf = (system: MeasurementSystem): DistanceUnit => (system === 'metric' ? 'km' : 'mi');

/**
 * The brand's own distance rules after a unit change, as the server will save
 * them (FR-018), for the confirmation. Empty when the brand sets none.
 */
export function convertedRulesPreview(
  rules: Rules,
  from: MeasurementSystem,
  to: MeasurementSystem,
  currency: CurrencyCode,
): string[] {
  if (from === to) return [];
  const a = unitOf(from);
  const b = unitOf(to);
  const per = (unit: DistanceUnit) => (unit === 'km' ? 'km' : 'mile');
  const lines: string[] = [];
  const mileage = rules?.mileage;
  if (mileage?.mode === 'perPeriod') {
    const miles = Number(mileage.miles);
    const price = Number(mileage.overagePerMile);
    lines.push(
      `Distance limit: ${formatDistance(miles, a, 'plain')} per ${mileage.days} days, ` +
        `${formatMoney(price, currency)} per extra ${per(a)} → ` +
        `${formatDistance(Math.max(1, convertLimit(miles, a, b)), b, 'plain')} per ${mileage.days} days, ` +
        `${formatMoney(convertPricePerUnit(price, a, b), currency)} per extra ${per(b)}`,
    );
  }
  const monthly = rules?.monthlyPricing;
  if (monthly?.mode === 'fromDaily' && monthly.monthlyMiles != null) {
    const value = Number(monthly.monthlyMiles);
    lines.push(
      `Monthly distance: ${formatDistance(value, a, 'plain')} → ` +
        `${formatDistance(Math.max(1, convertLimit(value, a, b)), b, 'plain')}`,
    );
  }
  const maxMiles = rules?.vehicleStandards?.maxMiles;
  if (maxMiles != null) {
    lines.push(
      `Highest odometer: ${formatDistance(Number(maxMiles), a, 'plain')} → ` +
        `${formatDistance(convertLimit(Number(maxMiles), a, b), b, 'plain')}`,
    );
  }
  return lines;
}

export interface RegionConfirm {
  title: string;
  body: string;
  confirm: string;
}

/** What the platform owner is asked before saving (spec US1 scenario 3). */
export function regionConfirm(
  brandName: string,
  loaded: RegionForm,
  next: RegionForm,
  rules: Rules,
): RegionConfirm {
  const parts: string[] = [];
  if (next.measurementSystem !== loaded.measurementSystem) {
    parts.push(
      next.measurementSystem === 'metric'
        ? `${brandName} will show and ask for distances in km and areas in m².`
        : `${brandName} will show and ask for distances in miles and areas in sq ft.`,
    );
    const preview = convertedRulesPreview(
      rules,
      loaded.measurementSystem,
      next.measurementSystem,
      next.currency,
    );
    if (preview.length) {
      parts.push(`${brandName}'s own distance rules are converted: ${preview.join('; ')}.`);
    }
  }
  if (next.currency !== loaded.currency) {
    parts.push(
      `Prices and charges on ${brandName} will be in ${next.currency}. ` +
        'The currency cannot change after the first trip is booked.',
    );
  }
  if (next.country !== loaded.country) {
    parts.push(
      `Address forms will ask for a ${next.country === 'CA' ? 'province and postal code' : 'state and ZIP code'}.`,
    );
  }
  parts.push('Trips already booked keep the units and currency they were booked in.');
  return {
    title: `Change the region of ${brandName}?`,
    body: parts.join(' '),
    confirm: 'Save region',
  };
}

/** History words: "Country: United States → Canada". */
export function describeRegionValue(key: string, value: unknown): string | null {
  if (key === 'country') return COUNTRY_LABELS[value === 'CA' ? 'CA' : 'US'];
  if (key === 'currency') return value === 'CAD' ? 'CAD' : 'USD';
  if (key === 'measurementSystem') return value === 'metric' ? 'Metric' : 'Imperial';
  return null;
}

export const REGION_TITLES: Record<string, string> = {
  country: 'Country',
  currency: 'Currency',
  measurementSystem: 'Units',
};

/** The message the server sends when the currency is locked, else null. */
export function currencyLockedMessage(error: unknown): string | null {
  const graphQLErrors = (
    error as {
      graphQLErrors?: Array<{
        extensions?: {
          code?: string;
          details?: { errors?: Array<{ path?: string; message?: string }> };
        };
      }>;
    }
  )?.graphQLErrors;
  for (const e of graphQLErrors ?? []) {
    if (e.extensions?.code !== 'BRAND_RULES_INVALID') continue;
    const hit = e.extensions.details?.errors?.find(x => x.path === 'bookingRules.currency');
    if (hit?.message) return hit.message;
  }
  return null;
}
